import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { tokens, surface, hairline } from '../../../styles/designTokens';
import { FiX, FiCalendar, FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import { GoOrganization } from 'react-icons/go';
import { IoCubeOutline } from 'react-icons/io5';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';

const NovoDownloadModal = ({
  isOpen,
  onClose,
  onStartDownload
}) => {
  const { currentProject } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [periodSummary, setPeriodSummary] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [availablePeriod, setAvailablePeriod] = useState({ min: null, max: null });

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
      fetchPeriodAndSetDates();
    }
  }, [isOpen]);

  const fetchPeriodAndSetDates = async () => {
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      if (!projectId) {
        setDefaultDates();
        return;
      }
      
      const response = await api.get(`/sped-download/stats/${projectId}`);
      
      if (response.data.success && response.data.stats && response.data.stats.periodMax) {
        const { periodMax } = response.data.stats;
        setAvailablePeriod({ min: response.data.stats.periodMin, max: periodMax });
        
        // Converter periodMax (dd/mm/yyyy) para Date
        const [day, month, year] = periodMax.split('/');
        const maxDate = new Date(year, month - 1, day);
        
        // Definir primeiro e último dia do mês do periodMax
        const firstDay = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
        const lastDay = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
        
        setStartDate(formatDateToBR(firstDay));
        setEndDate(formatDateToBR(lastDay));
        updatePeriodSummary(firstDay, lastDay);
      } else {
        setDefaultDates();
      }
    } catch (error) {
      console.error('Erro ao buscar período:', error);
      setDefaultDates();
    }
  };

  const setDefaultDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(formatDateToBR(firstDay));
    setEndDate(formatDateToBR(lastDay));
    updatePeriodSummary(firstDay, lastDay);
  };

  const formatDateToBR = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const parseBRDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return null;
    const [day, month, year] = dateStr.split('/');
    return new Date(year, month - 1, day);
  };

  const updatePeriodSummary = (start, end) => {
    if (!start || !end) return;
    
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      setPeriodSummary(`${months[start.getMonth()]}/${start.getFullYear()}`);
    } else {
      setPeriodSummary(`${months[start.getMonth()]}/${start.getFullYear()} - ${months[end.getMonth()]}/${end.getFullYear()}`);
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      if (!projectId) {
        console.warn('Nenhum projeto selecionado');
        setLoadingCompanies(false);
        return;
      }
      
      console.log('🔍 Buscando empresas para projeto:', projectId);
      
      const response = await api.get(`/certificates/list/${projectId}`);
      console.log('📡 Resposta completa:', response.data);
      
      if (response.data.success && response.data.data) {
        console.log('📦 Certificados recebidos:', response.data.data.length);
        
        // Transformar certificados em lista de empresas únicas
        const companiesMap = new Map();
        response.data.data.forEach(cert => {
          console.log('🏢 Processando certificado:', cert.cnpj, cert.companyName);
          if (!companiesMap.has(cert.cnpj)) {
            companiesMap.set(cert.cnpj, {
              id: cert.cnpj,
              name: cert.companyName,
              cnpj: cert.cnpj,
              city: cert.city,
              state: cert.state,
              color: cert.color || '#3b82f6'
            });
          }
        });
        
        const companies = Array.from(companiesMap.values());
        console.log('✅ Empresas únicas processadas:', companies.length, companies);
        setAvailableCompanies(companies);
        
        // Selecionar todas as empresas automaticamente
        setSelectedCompanies(companies.map(c => c.id));
        console.log('✅ Todas as empresas selecionadas automaticamente');
      } else {
        console.warn('⚠️ Resposta sem sucesso ou sem dados:', response.data);
        setAvailableCompanies([]);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar empresas:', error);
      console.error('Detalhes do erro:', error.response?.data);
      toast.error('Erro ao carregar empresas');
      setAvailableCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleStartDateChange = (e) => {
    const value = e.target.value;
    setStartDate(value);
    
    if (value.length === 10) {
      const date = parseBRDate(value);
      if (!date || isNaN(date.getTime())) {
        setStartDateError('Data inválida');
      } else if (date > new Date()) {
        setStartDateError('Data não pode ser futura');
      } else {
        setStartDateError('');
        const endDateObj = parseBRDate(endDate);
        if (endDateObj) {
          updatePeriodSummary(date, endDateObj);
        }
      }
    } else {
      setStartDateError('');
    }
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    setEndDate(value);
    
    if (value.length === 10) {
      const date = parseBRDate(value);
      const startDateObj = parseBRDate(startDate);
      
      if (!date || isNaN(date.getTime())) {
        setEndDateError('Data inválida');
      } else if (date > new Date()) {
        setEndDateError('Data não pode ser futura');
      } else if (startDateObj && date < startDateObj) {
        setEndDateError('Data final deve ser maior que inicial');
      } else {
        setEndDateError('');
        if (startDateObj) {
          updatePeriodSummary(startDateObj, date);
        }
      }
    } else {
      setEndDateError('');
    }
  };

  const goToPreviousMonth = () => {
    const currentStart = parseBRDate(startDate);
    if (!currentStart) return;
    
    const newStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 1);
    const newEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0);
    
    setStartDate(formatDateToBR(newStart));
    setEndDate(formatDateToBR(newEnd));
    updatePeriodSummary(newStart, newEnd);
  };

  const goToNextMonth = () => {
    const currentStart = parseBRDate(startDate);
    if (!currentStart) return;
    
    const newStart = new Date(currentStart.getFullYear(), currentStart.getMonth() + 1, 1);
    const newEnd = new Date(currentStart.getFullYear(), currentStart.getMonth() + 2, 0);
    
    if (newEnd > new Date()) return;
    
    setStartDate(formatDateToBR(newStart));
    setEndDate(formatDateToBR(newEnd));
    updatePeriodSummary(newStart, newEnd);
  };

  const canGoToNextMonth = () => {
    const currentStart = parseBRDate(startDate);
    if (!currentStart) return false;
    
    const newEnd = new Date(currentStart.getFullYear(), currentStart.getMonth() + 2, 0);
    return newEnd <= new Date();
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '';
    const cleaned = cnpj.replace(/\D/g, '');
    return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const cleanCompanyName = (name) => {
    if (!name) return '';
    return name.replace(/:\d+$/, '').trim();
  };

  const getColorFromText = (text) => {
    if (!text) return '#6b7280';
    
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const colors = [
      '#059669', '#10b981', '#2563eb', '#16a34a', '#0d9488',
      '#14b8a6', '#dc2626', '#0891b2', '#db2777', '#7c3aed',
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  const handleDateClick = (e) => {
    e.target.select();
  };

  const isValid = () => {
    return selectedCompanies.length > 0 && 
           startDate && 
           endDate && 
           !startDateError && 
           !endDateError;
  };

  const handleContinue = () => {
    if (!isValid()) return;
    
    const downloadData = {
      startDate: parseBRDate(startDate).toISOString().split('T')[0],
      endDate: parseBRDate(endDate).toISOString().split('T')[0],
      empresas: selectedCompanies
    };
    
    onStartDownload(downloadData);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-left">
            <div className="title-block">
              <span className="eyebrow">DOWNLOAD DE SPEDS · SEFAZ</span>
              <h2>Por Período e Empresas</h2>
            </div>
          </div>
          <button className="close-btn" onClick={onClose} title="Fechar">
            <FiX />
          </button>
        </ModalHeader>

        <ModalBody>
          {/* Header Fixo - Período de Análise */}
          <PeriodHeader>
            <PeriodHeaderTop>
              <SectionTitle>Período de Download</SectionTitle>
              {periodSummary && (
                <PeriodBadgeRow>
                  <NavButton onClick={goToPreviousMonth} title="Mês anterior">
                    <FiChevronLeft />
                  </NavButton>
                  <PeriodBadge>
                    <FiCalendar />
                    {periodSummary}
                  </PeriodBadge>
                  <NavButton 
                    onClick={goToNextMonth} 
                    disabled={!canGoToNextMonth()}
                    title="Próximo mês"
                  >
                    <FiChevronRight />
                  </NavButton>
                </PeriodBadgeRow>
              )}
            </PeriodHeaderTop>
            
            <DateInputsRow>
              <DateInputGroup>
                <DateInput
                  type="text"
                  placeholder="01/04/2026"
                  value={startDate}
                  onChange={handleStartDateChange}
                  onClick={handleDateClick}
                  maxLength={10}
                  $hasError={!!startDateError}
                />
                {startDateError && <ErrorMessage>{startDateError}</ErrorMessage>}
              </DateInputGroup>

              <DateSeparator>até</DateSeparator>

              <DateInputGroup>
                <DateInput
                  type="text"
                  placeholder="20/04/2026"
                  value={endDate}
                  onChange={handleEndDateChange}
                  onClick={handleDateClick}
                  maxLength={10}
                  $hasError={!!endDateError}
                />
                {endDateError && <ErrorMessage>{endDateError}</ErrorMessage>}
              </DateInputGroup>
            </DateInputsRow>
          </PeriodHeader>

          {/* Toolbar - Empresas */}
          <CompaniesToolbar>
            <div className="toolbar-left">
              <GoOrganization />
              <span>Selecione as Empresas</span>
            </div>
            {selectedCompanies.length > 0 && (
              <SelectedCount>{selectedCompanies.length} selecionada{selectedCompanies.length !== 1 ? 's' : ''}</SelectedCount>
            )}
          </CompaniesToolbar>

          {/* Lista de Empresas - Scrollable */}
          <CompaniesScrollArea>
            {loadingCompanies ? (
              <EmptyState>
                <GoOrganization />
                <p>Carregando empresas...</p>
              </EmptyState>
            ) : availableCompanies.length === 0 ? (
              <EmptyState>
                <GoOrganization />
                <p>Nenhuma empresa disponível</p>
              </EmptyState>
            ) : (
              <CompaniesGrid>
                {availableCompanies.map(company => {
                  const isSelected = selectedCompanies.includes(company.id);
                  
                  return (
                    <CompanyCard
                      key={company.id}
                      $selected={isSelected}
                      onClick={() => toggleCompany(company.id)}
                    >
                      <CompanyAvatar $color={company.color || '#3b82f6'}>
                        <IoCubeOutline />
                      </CompanyAvatar>

                      <CompanyInfo>
                        <CompanyName>{cleanCompanyName(company.name)}</CompanyName>
                        <NotionTags>
                          {company.cnpj && (
                            <NotionTag className="cnpj-tag">
                              {formatCNPJ(company.cnpj)}
                            </NotionTag>
                          )}
                          {company.city && (
                            <NotionTag className="city-tag" $color={getColorFromText(company.city)}>
                              {company.city}
                            </NotionTag>
                          )}
                          {company.state && (
                            <NotionTag className="state-tag" $color={getColorFromText(company.state)}>
                              {company.state}
                            </NotionTag>
                          )}
                        </NotionTags>
                      </CompanyInfo>
                    </CompanyCard>
                  );
                })}
              </CompaniesGrid>
            )}
          </CompaniesScrollArea>
        </ModalBody>

        <ModalFooter>
          <FooterButton onClick={onClose}>
            Cancelar
          </FooterButton>
          <FooterButton 
            $primary
            onClick={handleContinue}
            disabled={!isValid()}
          >
            <FiDownload />
            Processar Download
          </FooterButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default NovoDownloadModal;

// ============= STYLED COMPONENTS =============

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: 0.6em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(12px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${fadeIn} ${tokens.durBase} ${tokens.easeOut};
`;

const ModalContainer = styled.div`
  position: relative;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusLg};
  width: 100%;
  max-width: 560px;
  height: min(720px, 90vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow:
    0 1px 0 0 rgba(255, 255, 255, 0.04) inset,
    0 24px 64px rgba(0, 0, 0, 0.45),
    0 8px 16px rgba(0, 0, 0, 0.18);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${tokens.brandRing}, transparent);
    pointer-events: none;
    z-index: 2;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 20px 14px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  .header-left {
    flex: 1;
    min-width: 0;
  }

  .title-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;

    .eyebrow {
      ${eyebrow};
      font-size: 0.58em;
      opacity: 0.4;
    }

    h2 {
      margin: 2px 0 0 0;
      font-size: 1.05em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.015em;
      line-height: 1.2;
    }
  }

  .close-btn {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.65;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    flex-shrink: 0;

    svg { font-size: 1em; }

    &:hover:not(:disabled) {
      opacity: 1;
      border-color: ${({ theme }) => hairline(theme, true)};
    }

    &:disabled {
      opacity: 0.25;
      cursor: not-allowed;
    }
  }
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const PeriodHeader = styled.div`
  padding: 14px 20px 12px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
`;

const PeriodHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const SectionTitle = styled.h4`
  ${eyebrow};
  margin: 0;
  font-size: 0.6em;
  opacity: 0.55;
`;

const CompaniesToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 20px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  .search-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    flex: 1;
    max-width: 360px;

    &:focus-within { border-color: ${tokens.brandRing}; }

    svg { font-size: 0.85em; opacity: 0.4; }

    input {
      flex: 1;
      background: transparent;
      border: none;
      outline: none;
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.78em;

      &::placeholder { opacity: 0.4; }
    }
  }
`;

const SelectedCount = styled.span`
  ${mono};
  font-size: 0.65em;
  font-weight: 500;
  color: ${tokens.brand};
  background: ${tokens.brandSoft};
  border: 1px solid ${tokens.brandRing};
  padding: 4px 10px;
  border-radius: ${tokens.radiusPill};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const CompaniesScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 20px;
  min-height: 0;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

const DateInputsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DateInputGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 130px;

  label {
    ${eyebrow};
    font-size: 0.55em;
    opacity: 0.55;
  }
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border-radius: ${tokens.radiusSm};
  border: 1px solid ${({ theme, $hasError }) => $hasError ? tokens.danger : hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  color: ${({ theme }) => theme.text};
  font-family: ${tokens.fontMono};
  font-size: 0.78em;
  letter-spacing: 0.01em;
  outline: none;
  transition: border-color ${tokens.durBase} ${tokens.easeOut};

  &::placeholder { opacity: 0.4; }
  &:focus { border-color: ${tokens.brandRing}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMessage = styled.span`
  ${mono};
  font-size: 0.62em;
  color: ${tokens.danger};
  letter-spacing: 0.01em;
  margin-top: 2px;
`;

const DateSeparator = styled.span`
  ${mono};
  font-size: 0.7em;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  align-self: flex-end;
  padding-bottom: 9px;
`;

const PeriodBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NavButton = styled.button`
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusSm};
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  svg { font-size: 0.85em; }

  &:hover:not(:disabled) {
    opacity: 1;
    background: ${({ theme }) => surface(theme, 1)};
    border-color: ${({ theme }) => hairline(theme, true)};
  }

  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const PeriodBadge = styled.div`
  ${mono};
  padding: 4px 10px;
  background: ${tokens.brandSoft};
  color: ${tokens.brand};
  border: 1px solid ${tokens.brandRing};
  border-radius: ${tokens.radiusPill};
  font-size: 0.66em;
  font-weight: 500;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  svg { font-size: 0.95em; }
`;

const CompaniesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const CompanyCard = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  align-items: center;
  padding: 8px 12px;
  background: ${({ theme, $selected }) =>
    $selected ? surface(theme, 1) : 'transparent'};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusSm};
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  opacity: ${({ $selected }) => ($selected ? 1 : 0.62)};

  &:hover {
    background: ${({ theme }) => surface(theme, 1)};
    border-color: ${({ theme }) => hairline(theme, true)};
    opacity: 1;
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const CompanyName = styled.div`
  font-size: 0.78em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  letter-spacing: -0.005em;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NotionTags = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 2px;
`;

const NotionTag = styled.span`
  ${mono};
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: ${tokens.radiusPill};
  font-size: 0.55em;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid;
  background: ${({ $color }) => ($color ? `${$color}14` : 'transparent')};
  color: ${({ $color, theme }) => $color || theme.text};
  border-color: ${({ $color, theme }) => ($color ? `${$color}38` : hairline(theme))};

  /* CNPJ tag: neutro mono sem cor */
  &.cnpj-tag {
    background: ${({ theme }) => surface(theme, 1)};
    color: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => hairline(theme)};
    opacity: 0.7;
    font-size: 0.58em;
  }
`;

const CompanyAvatar = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color }) => ($color ? `${$color}1f` : tokens.brandSoft)};
  color: ${({ $color }) => $color || tokens.brand};
  border-radius: ${tokens.radiusSm};
  ${mono};
  font-size: 0.85em;
  font-weight: 500;
  flex-shrink: 0;

  svg { font-size: 0.95em; }
`;

const KpisRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;

  @media (max-width: 700px) { grid-template-columns: repeat(2, 1fr); }
`;

const KpiCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover { border-color: ${({ theme }) => hairline(theme, true)}; }
`;

const KpiIcon = styled.div`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${tokens.brandSoft};
  border: 1px solid ${tokens.brandRing};
  border-radius: ${tokens.radiusSm};
  color: ${tokens.brand};
  flex-shrink: 0;

  svg { font-size: 0.95em; }
`;

const KpiContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const KpiValue = styled.div`
  ${mono};
  font-size: 0.92em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const KpiLabel = styled.div`
  ${eyebrow};
  font-size: 0.55em;
  opacity: 0.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 20px;
  text-align: center;

  svg {
    width: 56px;
    height: 56px;
    padding: 16px;
    box-sizing: content-box;
    background: ${tokens.brandSoft};
    border: 1px solid ${tokens.brandRing};
    border-radius: 50%;
    color: ${tokens.brand};
    opacity: 0.7;
    font-size: 1em;
  }

  p {
    margin: 0;
    font-size: 0.9em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    letter-spacing: -0.005em;
  }

  small {
    ${mono};
    font-size: 0.7em;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    max-width: 280px;
    line-height: 1.5;
    letter-spacing: 0.01em;
  }
`;

const ModalFooter = styled.div`
  position: relative;
  flex-shrink: 0;
  padding: 12px 18px 14px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => hairline(theme, true)}, transparent);
    opacity: 0.4;
    pointer-events: none;
  }
`;

const FooterButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: ${tokens.radiusSm};
  font-family: ${tokens.fontSans};
  font-size: 0.78em;
  font-weight: 500;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  letter-spacing: -0.005em;
  white-space: nowrap;
  background: transparent;
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => hairline(theme)};
  opacity: 0.85;

  svg { font-size: 0.95em; }

  &:hover:not(:disabled) {
    background: ${({ theme }) => surface(theme, 1)};
    border-color: ${({ theme }) => hairline(theme, true)};
    opacity: 1;
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }

  ${({ $primary }) =>
    $primary &&
    css`
      background: ${tokens.brand};
      border-color: ${tokens.brand};
      color: white;
      opacity: 1;
      box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.08) inset;

      &:hover:not(:disabled) {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }
    `}
`;
